/**
 * Company Dashboard
 * Overview for company administrators with real-time stats
 */

import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Company } from "../../../shared/types";
import { NavCard, PageHeader } from "../components/common";
import {
  DashboardGrid,
  LoadingState,
  StatCard,
  StatIcons,
  TeamAccountsCard,
} from "../components/dashboard";
import { db } from "../config/firebase";
import { BORDER_RADIUS, COLORS, SPACING, TYPOGRAPHY } from "../constants/theme";
import { useAuth } from "../hooks/useAuth";
import { getCompanyById } from "../services/company.service";

export function CompanyDashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalStaff: 0,
    activeToday: 0,
    pendingEntries: 0,
    todaysJobs: 0,
  });

  useEffect(() => {
    const companyId = currentUser?.companyId;
    if (!companyId) return;
    getCompanyById(companyId)
      .then((data) => {
        if (data) setCompany(data);
      })
      .catch((err) => console.error("Error loading company:", err))
      .finally(() => setLoading(false));
  }, [currentUser?.companyId]);

  // Real-time statistics
  useEffect(() => {
    if (!currentUser?.companyId) return;

    const companyId = currentUser.companyId;

    // Listen to users for staff count (includes both staff and managers)
    const usersQuery = query(
      collection(db, "Users"),
      where("companyId", "==", companyId),
    );

    const unsubscribeUsers = onSnapshot(
      usersQuery,
      (snapshot) => {
        // Count staff and managers only (exclude company admin)
        const totalStaff = snapshot.docs.filter((doc) => {
          const userType = doc.data().userType;
          return userType === "staff" || userType === "manager";
        }).length;
        setStats((prev) => ({ ...prev, totalStaff }));
      },
      () => {},
    );

    // Listen to jobs for today's jobs count
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const jobsQuery = query(
      collection(db, "OneTimeJobs"),
      where("companyId", "==", companyId),
      where("scheduledStart", ">=", today),
      where("scheduledStart", "<", tomorrow),
    );

    const unsubscribeJobs = onSnapshot(
      jobsQuery,
      (snapshot) => {
        const todaysJobs = snapshot.size;
        setStats((prev) => ({ ...prev, todaysJobs }));
      },
      () => {},
    );

    // Listen to time entries for active today count
    const timeEntriesQuery = query(
      collection(db, "TimeEntries"),
      where("companyId", "==", companyId),
      where("status", "==", "active"),
    );

    const unsubscribeTimeEntries = onSnapshot(
      timeEntriesQuery,
      (snapshot) => {
        const activeToday = snapshot.size;
        setStats((prev) => ({ ...prev, activeToday }));
      },
      () => {},
    );

    // Listen to pending time entries
    const pendingEntriesQuery = query(
      collection(db, "TimeEntries"),
      where("companyId", "==", companyId),
      where("status", "==", "pending"),
    );

    const unsubscribePending = onSnapshot(
      pendingEntriesQuery,
      (snapshot) => {
        const pendingEntries = snapshot.size;
        setStats((prev) => ({ ...prev, pendingEntries }));
      },
      () => {},
    );

    return () => {
      unsubscribeUsers();
      unsubscribeJobs();
      unsubscribeTimeEntries();
      unsubscribePending();
    };
  }, [currentUser?.companyId]);

  if (loading) {
    return <LoadingState message="Loading company dashboard..." />;
  }

  return (
    <div style={{ padding: SPACING["2xl"] }}>
      <PageHeader
        title="Dashboard"
        subtitle="Overview of your company's activity and operations"
      />

      {/* Stats Grid */}
      <DashboardGrid columns={4}>
        {company && (
          <TeamAccountsCard
            teamSize={stats.totalStaff}
            totalUsers={stats.totalStaff}
            maxUsers={company.maxUsers}
          />
        )}
        <StatCard
          title="Active Today"
          value={stats.activeToday}
          icon={StatIcons.checkCircle}
          color={COLORS.success.light}
          iconColor={COLORS.success.dark}
          subtitle="Staff checked in"
        />
        <StatCard
          title="Today's Jobs"
          value={stats.todaysJobs}
          icon={StatIcons.clipboard}
          color={COLORS.warning.light}
          iconColor={COLORS.warning.dark}
          subtitle="Scheduled for today"
        />
        <StatCard
          title="Pending Approval"
          value={stats.pendingEntries}
          icon={StatIcons.clock}
          color="#ede9fe"
          iconColor="#7c3aed"
          subtitle="Awaiting review"
        />
      </DashboardGrid>

      {/* Quick Actions */}
      <div
        style={{
          backgroundColor: COLORS.background.secondary,
          padding: SPACING.xl,
          borderRadius: BORDER_RADIUS.lg,
          marginTop: SPACING["3xl"],
        }}
      >
        <h2
          style={{
            fontSize: TYPOGRAPHY.fontSize.lg,
            fontWeight: TYPOGRAPHY.fontWeight.semibold,
            color: COLORS.text.primary,
            marginBottom: SPACING.lg,
          }}
        >
          Quick Actions
        </h2>
        <div style={{ display: "flex", gap: SPACING.md, flexWrap: "wrap" }}>
          <NavCard
            label="Manage Jobs"
            description="Create and assign jobs"
            icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" ry="1" /></svg>}
            onClick={() => navigate("/jobs")}
          />
          <NavCard
            label="Manage Staff"
            description="View and invite team members"
            icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>}
            onClick={() => navigate("/staff")}
          />
          <NavCard
            label="Time Entries"
            description="Review and approve entries"
            icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>}
            onClick={() => navigate("/approvals")}
          />
        </div>
      </div>
    </div>
  );
}
